import { lazy } from 'react';
import { wrapCreateBrowserRouter } from '@sentry/react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  Navigate,
} from 'react-router-dom';
import ErrorBoundary from 'common/components/error-boundary/error-boundary.component.tsx';
import LazyComponent from 'common/components/lazy-component/lazy-component.component.tsx';
import { loginPageLoader } from 'pages/auth/login/login.page.loader.ts';
import { billingDetailsPageLoader } from 'pages/billing-details/billing-details.page.loader.ts';
import { errorPageLoader } from 'pages/error/error.page.loader.ts';
import { finalStepPageLoader } from 'pages/final-step/final-step.page.loader.ts';
import { kycPageLoader } from 'pages/kyc/kyc.page.loader.ts';
import { noMatchPageLoader } from 'pages/no-match/no-match.page.loader.ts';
import { rampPageLoader } from 'pages/ramp/ramp.page.loader.ts';
import { transactionPageLoader } from 'pages/transaction/transaction.page.loader.ts';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants.ts';
import { neutralRouteGuardRootLoader } from 'navigation/guards/neutralRouteGuardRootLoader.loader.tsx';

// Disabled in case of the issue https://github.com/ArnaudBarre/eslint-plugin-react-refresh/issues/25
// eslint-disable-next-line react-refresh/only-export-components
const MainLayout = lazy(() => import('common/layouts/main/main.layout.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const SecondPageLayout = lazy(() => import('common/layouts/second-page/second-page.layout.tsx'));

// eslint-disable-next-line react-refresh/only-export-components
const NeutralRouteGuard = lazy(() => import('navigation/guards/NeutralRoute.guard.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const ProtectedRouteGuard = lazy(() => import('./guards/ProtectedRoute.guard.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const PublicRouteGuard = lazy(() => import('./guards/PublicRoute.guard.tsx'));

// eslint-disable-next-line react-refresh/only-export-components
const BillingDetailsPage = lazy(() => import('pages/billing-details/billing-details.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const ProfilePage = lazy(() => import('pages/profile/profile.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const NoMatchPage = lazy(() => import('pages/no-match/no-match.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const ErrorPage = lazy(() => import('pages/error/error.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const SecurityHistoryPage = lazy(() => import('pages/security-history/security-history.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const SecurityPage = lazy(() => import('pages/security/security.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const TransactionPage = lazy(() => import('pages/transaction/transaction.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const TransactionsPage = lazy(() => import('pages/transactions/transactions.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const FpfPaymentPage = lazy(() => import('pages/fpf-payment/fpf-payment.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const InteractiveFlowPage = lazy(() => import('pages/interactive-flow/interactive-flow.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const OtpPage = lazy(() => import('pages/auth/otp/otp.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const LoginGooglePage = lazy(() => import('pages/auth/login/login-google.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const LoginPage = lazy(() => import('pages/auth/login/login.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const TermsAndPrivacyPage = lazy(() => import('pages/terms-and-privacy/terms-and-privacy.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const Widget = lazy(() => import('pages/widget/widget.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const FinalStepPage = lazy(() => import('pages/final-step/final-step.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const KycPage = lazy(() => import('pages/kyc/kyc.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const OrderFormPage = lazy(() => import('pages/order-form/order-form.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const TestSentryErrorPage = lazy(() => import('pages/test-sentry-error/test-sentry-error.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const FpfOrderFormPage = lazy(() => import('pages/fpf-order-form/fpf-order-form.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const DecisionFlowPage = lazy(() => import('pages/decision-flow/decision-flow.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const PaymentOptionsUnavailablePage =
  lazy(() => import('pages/payment-options-unavailable/payment-options-unavailable.page.tsx'));
// eslint-disable-next-line react-refresh/only-export-components
const RampPage =
  lazy(() => import('pages/ramp/ramp.page.tsx'));

const routes = (
  <Route
    path={ROUTES_MAPPING.ROOT}
    // This option allows you to unlock the React Query cache for a second request
    shouldRevalidate={({ currentUrl }): boolean => currentUrl.pathname === ROUTES_MAPPING.PUBLIC.LOGIN}
    errorElement={<ErrorBoundary />}
  >
    <Route
      element={
        <LazyComponent>
          <NeutralRouteGuard />
        </LazyComponent>
      }
      loader={neutralRouteGuardRootLoader}
    >
      <Route index element={<Navigate to={ROUTES_MAPPING.PUBLIC.LOGIN} />} />

      {/* Neutral routes */}
      <Route
        element={
          <LazyComponent>
            <MainLayout />
          </LazyComponent>
        }
      >
        <Route
          path={ROUTES_MAPPING.PUBLIC.TERMS_AND_CONDITIONS}
          element={
            <LazyComponent>
              <TermsAndPrivacyPage
                url="https://aps.exchange/ramp-terms-and-conditions"
                title="termsAndConditions.title"
              />
            </LazyComponent>
          }
        />
        <Route
          path={ROUTES_MAPPING.PUBLIC.PRIVACY_AND_COOKIES_POLICY}
          element={
            <LazyComponent>
              <TermsAndPrivacyPage
                url="https://aps.exchange/ramp-cookies-privacy-policy"
                title="privacyAndCookiesPolicy.title"
              />
            </LazyComponent>
          }
        />
        <Route path={ROUTES_MAPPING.DECISION_FLOW} element={<DecisionFlowPage />} />
        <Route path={ROUTES_MAPPING.FPF_ORDER_FORM} element={<FpfOrderFormPage />} />
        <Route path={ROUTES_MAPPING.TEST_SENTRY_ERROR} element={<TestSentryErrorPage />} />
        <Route path={ROUTES_MAPPING.ORDER_FORM} element={<OrderFormPage />} />
        <Route
          path={ROUTES_MAPPING.PAYMENT_OPTIONS_UNAVAILABLE}
          element={
            <LazyComponent>
              <PaymentOptionsUnavailablePage />
            </LazyComponent>
          }
        />
        <Route
          path={ROUTES_MAPPING.RAMP}
          element={
            <LazyComponent>
              <RampPage />
            </LazyComponent>
          }
          loader={rampPageLoader}
        />
      </Route>
      <Route
        path={ROUTES_MAPPING.PUBLIC.WIDGET}
        element={
          <LazyComponent>
            <Widget />
          </LazyComponent>
        }
      />
      <Route
        path={ROUTES_MAPPING.FINAL_STEP}
        element={
          <LazyComponent>
            <FinalStepPage />
          </LazyComponent>
        }
        loader={finalStepPageLoader}
      />

      {/* Public routes */}
      <Route
        element={
          <LazyComponent>
            <PublicRouteGuard />
          </LazyComponent>
        }
      >
        <Route
          element={
            <LazyComponent>
              <MainLayout />
            </LazyComponent>
          }
        >
          <Route
            path={ROUTES_MAPPING.PUBLIC.LOGIN}
            element={
              <LazyComponent>
                <LoginPage />
              </LazyComponent>
            }
            loader={loginPageLoader}
          />
          <Route
            path={ROUTES_MAPPING.PUBLIC.LOGIN_GOOGLE}
            element={
              <LazyComponent>
                <LoginGooglePage />
              </LazyComponent>
            }
            loader={loginPageLoader}
          />
          <Route
            path={ROUTES_MAPPING.PUBLIC.OTP_CODE_VERIFICATION}
            element={
              <LazyComponent>
                <OtpPage />
              </LazyComponent>
            }
          />
        </Route>
      </Route>
      {/* Protected routes */}
      <Route
        element={
          <LazyComponent>
            <ProtectedRouteGuard />
          </LazyComponent>
        }
      >
        <Route
          element={
            <LazyComponent>
              <MainLayout />
            </LazyComponent>
          }
        >
          <Route
            path={ROUTES_MAPPING.PROTECTED.INTERACTIVE_FLOW}
            element={
              <LazyComponent>
                <InteractiveFlowPage />
              </LazyComponent>
            }
          />
          <Route
            path={ROUTES_MAPPING.PROTECTED.BILLING_DETAILS}
            element={
              <LazyComponent>
                <BillingDetailsPage />
              </LazyComponent>
            }
            loader={billingDetailsPageLoader}
          />
          <Route
            path={ROUTES_MAPPING.PROTECTED.KYC}
            element={
              <LazyComponent>
                <KycPage />
              </LazyComponent>
            }
            loader={kycPageLoader}
          />
          <Route
            path={ROUTES_MAPPING.PROTECTED.FPF_PAYMENT}
            element={
              <LazyComponent>
                <FpfPaymentPage />
              </LazyComponent>
            }
          />
          <Route
            path={ROUTES_MAPPING.PROTECTED.TRANSACTIONS}
            element={
              <LazyComponent>
                <TransactionsPage />
              </LazyComponent>
            }
          />
          <Route
            path={ROUTES_MAPPING.PROTECTED.TRANSACTION}
            element={
              <LazyComponent>
                <TransactionPage />
              </LazyComponent>
            }
            loader={transactionPageLoader}
          />
        </Route>

        <Route
          element={
            <LazyComponent>
              <SecondPageLayout />
            </LazyComponent>
          }
        >
          <Route
            path={ROUTES_MAPPING.PROTECTED.PROFILE}
            element={
              <LazyComponent>
                <ProfilePage />
              </LazyComponent>
            }
          />
        </Route>

        <Route
          path={ROUTES_MAPPING.PROTECTED.SECURITY}
          element={
            <LazyComponent>
              <SecondPageLayout />
            </LazyComponent>
          }
        >
          <Route
            index
            element={
              <LazyComponent>
                <SecurityPage />
              </LazyComponent>
            }
          />
          <Route
            path={ROUTES_MAPPING.PROTECTED.SECURITY_HISTORY}
            element={
              <LazyComponent>
                <SecurityHistoryPage />
              </LazyComponent>
            }
          />
        </Route>
      </Route>

      <Route
        element={
          <LazyComponent>
            <MainLayout />
          </LazyComponent>
        }
      >
        <Route
          path={ROUTES_MAPPING.ERROR}
          element={
            <LazyComponent>
              <ErrorPage />
            </LazyComponent>
          }
          loader={errorPageLoader}
        />
      </Route>

      <Route
        path={ROUTES_MAPPING.NO_MATCH}
        element={
          <LazyComponent>
            <MainLayout />
          </LazyComponent>
        }
      >
        <Route
          index
          element={
            <LazyComponent>
              <NoMatchPage />
            </LazyComponent>
          }
          loader={noMatchPageLoader}
        />
      </Route>
      <Route path="*" element={<Navigate to={ROUTES_MAPPING.NO_MATCH} />} />
    </Route>
  </Route>
);

const sentryCreateBrowserRouter = wrapCreateBrowserRouter(createBrowserRouter);
const routerTree = sentryCreateBrowserRouter(createRoutesFromElements(routes));

export default routerTree;
